import { TemplatePortal } from '@angular/cdk/portal';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { Component, ElementRef, TemplateRef, ViewChild, ViewContainerRef, AfterViewInit, HostListener, OnDestroy } from '@angular/core';
import { DialerService } from '../dialer.service';
import { LocalStorageService } from 'app/core/services/localstorage.service';
import { environment } from 'environments/environment';
import { SharedModule } from 'app/shared/shared.module';
import { UserService } from 'app/core/user/user.service';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { Subject, Subscription, takeUntil } from 'rxjs';

@Component({
    templateUrl: './dialer-overlay.component.html',
    styleUrls: ['./dialer-overlay.component.scss'],
    selector: 'zuper-dialer-overlay',
    standalone: true,
    imports: [SharedModule]
})
export class DialerOverlayComponent implements AfterViewInit, OnDestroy {
    private _dialerOrigin: ElementRef<HTMLButtonElement>;
    @ViewChild(CdkDrag, { static: false }) drag?: CdkDrag;
    @ViewChild('dialerPanel') private _dialerPanel: TemplateRef<any>;
    @ViewChild('dragger') private _dragger: ElementRef<HTMLElement>;
    isCallFromCustomer: boolean | null;
    callToName: string | null;
    isCallToCustomer: boolean | null;
    callFromName: string | null;
    callerName: any;
    shouldOpenDialer: boolean = false;
    @ViewChild('ringCentralIframe') set ringCentralIframe(elementRef: ElementRef<HTMLIFrameElement>) {
        if (elementRef) {
            this._ringCentralIframe = elementRef;
            this._dialerService.initializeRingCentralMessageListener(this._ringCentralIframe);
        }
    }
    @ViewChild('zuperConnectIframe') set zuperConnectIframe(elementRef: ElementRef<HTMLIFrameElement>) {
        if (elementRef) {
            this._zuperConnectIframe = elementRef;
            this._dialerService.initializeZuperConnectMessageListener(this._zuperConnectIframe);
            this.shouldOpenDialer = true
            // Note: Removed onload handler that was showing overlay immediately
            // The overlay will be shown after receiving agent_status event from iframe
            // This prevents showing dialer when agent is BUSY on another tab
        }
    }
    integratedDialerPluginNames = environment.integratedDialers;
    dialerDimensions: any = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        [this.integratedDialerPluginNames.ringcentral]: {
            width: 300,
            height: 535
        },
        [this.integratedDialerPluginNames.aircall]: {
            width: 376,
            height: 600,
        }
    };
    showDialerButton: boolean = false;
    overlayHeight: number;
    overlaywidth: number;
    toggleOverlay: boolean = false;
    isPanelHidden: boolean = false
    dialerOriginPosition = { top: '3rem', left: '3rem' };
    integrations: any;
    airCallInstance: any;
    onCall: boolean = false;
    onCalling: boolean = false;
    zuperConnectUrl: SafeResourceUrl;
    reset: any;
    isZuperConnectMinimizing: boolean = false;
    isZuperConnectMinimized: boolean;
    dragPosition = { x: 0, y: 0 };
    isUserAvailable = true;
    isUserOnCallOtherTab: boolean | undefined = localStorage.getItem('activeCallOnOtherTab') === 'true';
    currentUserStatus: string = 'ONLINE';
    isLightThemed: boolean = false;
    isContextualDisplayOpen: boolean = false;
    isNotIntraction: boolean = false;
    private _ringCentralIframe: ElementRef<HTMLIFrameElement>;
    private _zuperConnectIframe: ElementRef<HTMLIFrameElement>;
    private _overlayRef: OverlayRef;
    private _subscriptions: Subscription[] = [];
    private readonly destroy$ = new Subject<void>();

    constructor(
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _dialerService: DialerService,
        private _localStorageService: LocalStorageService,
        private _domSanitizer: DomSanitizer,
        private _userService: UserService,
    ) {

    }

    ngAfterViewInit() {
        this._dialerService.dialerOriginSub$.pipe(takeUntil(this.destroy$)).subscribe((elRef: ElementRef<HTMLButtonElement>) => {
            if (elRef) {
                this.initDialerOverlayIntegration(elRef);
            }
        });
        this._subscriptions.push(

            this._dialerService.toogleDialerSub$.subscribe((toogle) => this.togglePanel(toogle)),
            this._dialerService.callEvents.subscribe((res) => {
                console.log('Events', res)
                this._handleCallEvent(res);
            }),
            this._dialerService.isUserOnCallOtherTab.subscribe((isCallConnected: boolean) => {
                this.isUserOnCallOtherTab = isCallConnected;
                if (this.toggleOverlay) {
                    this.togglePanel(false);
                }
            }),
            this._userService.logout$.subscribe((isLoggedOut: boolean) => {
                if (isLoggedOut) {
                    // removing overlay and set undefined so that panel will be recreated on next login
                    this._overlayRef?.dispose();
                    this._overlayRef = undefined;
                    this._dialerOrigin = undefined;
                }
            })
        );
    }

	/**
	 * Get the current sidekick enabled status from company policy
	 * This getter ensures we always have the latest value, avoiding race conditions during initialization
	 * @returns boolean indicating if sidekick is enabled
	 */
	get isSideKickEnabled(): boolean {
		const companyPolicy = this._localStorageService.getLocalStorage('company_policy');
		return companyPolicy?.zuper_connect?.voice?.sidekick_enabled || false;
	}

    ngOnDestroy() {
        // Clear dialer minimized state when tab closes
        this._dialerService.clearDialerMinimizedState();

        this.destroy$.next();
        this.destroy$.complete();
        this._subscriptions.forEach((s) => s.unsubscribe());
        this._overlayRef?.dispose();
        this._overlayRef = undefined;
        this._dialerOrigin = undefined
    }

    initDialerOverlayIntegration(dialerOrigin: ElementRef<HTMLButtonElement>): void {
        if (this._dialerOrigin) {
            this._dialerOrigin = dialerOrigin;
            return;
        }
        this._dialerOrigin = dialerOrigin;
        this._subscriptions.push(
            this._dialerService.fetchDialerIntegrations().subscribe((integrations: any) => {
                this.integrations = integrations;
                if (Object.keys(this.integrations).length > 0) {
                    if (this.integrations['zuperconnect']) {
                        const company = this._localStorageService.getLocalStorage('company');
                        const url = `${environment.callerUrl}?companyLoginName=${company?.company_login_name}&zuperAuthToken=${this._localStorageService.getLocalStorage('auth_token')}`;
                        this.zuperConnectUrl = this._domSanitizer.bypassSecurityTrustResourceUrl(url);
                    } else {
                        this.isLightThemed = true;
                    }
                    this.togglePanel(true, true);
                }
            })
        );
    }

    togglePanel(toogle: boolean, init = false): void {
        if (this.onCall || this.onCalling) {
            return;
        }
        this.toggleOverlay = init ? false : toogle;
        if (toogle) {
            if (!this._overlayRef) {
                this._createOverlay();
                this._overlayRef.attach(new TemplatePortal(this._dialerPanel, this._viewContainerRef));
                if (this.integrations?.[this.integratedDialerPluginNames.aircall] && !this.airCallInstance) {
                    this.airCallInstance = this._dialerService.initAircallIntegration('#aircall-container');
                }
            }
            if (init) {
                this._overlayRef.overlayElement.style.display = 'none';
                setTimeout(() => {
                    this._overlayRef.overlayElement.classList.add('overlay-zoom');
                }, 0);
                this._dialerService.isDialerOverlayReady.next(true);
                this.isNotIntraction = true;
            } else {
                this._overlayRef.overlayElement.style.display = 'block';
                setTimeout(() => {
                    this._overlayRef.overlayElement.classList.remove('zoom-out');
                    this._overlayRef.overlayElement.classList.add('zoom-in');
                }, 0)
            }
        } else {
            if (!this._overlayRef) return;
            this._overlayRef.overlayElement.classList.remove('zoom-in');
            this._overlayRef.overlayElement.classList.add('zoom-out');
            this.isNotIntraction = false;
            setTimeout(() => {
                // waiting for animation to end
                this._maximizeZuperConnectFrame();
            }, 300)
        }
    }

    onDragEnd(_: any) {
    }

    private _getMaximizedPosition(): FlexibleConnectedPositionStrategy {
        return this._overlay.position().flexibleConnectedTo(this._dialerOrigin.nativeElement)
            .withLockedPosition(false)
            .withPush(true)
            .withPositions([
                {
                    originX: "end",
                    originY: "bottom",
                    overlayX: "end",
                    overlayY: "top"
                }
            ]);
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: false,
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._getMaximizedPosition(),
            panelClass: 'pointer-events-none'
        });
    }

    private _getPositionStrategy(currentPosition?: any): FlexibleConnectedPositionStrategy {

        const positions: any =
        {
            originX: 'center',
            originY: 'center',
            overlayX: 'center',
            overlayY: 'center'
        };

        if (currentPosition) {
            // Adjust horizontal position based on currentPosition.x
            if (currentPosition?.x === 'left') {
                positions.originX = 'end';
                positions.overlayX = 'start';  // The overlay appears to the left of the origin
            } else if (currentPosition?.x === 'right') {
                positions.originX = 'start';
                positions.overlayX = 'end';  // The overlay appears to the right of the origin
            }

            // Adjust vertical position based on currentPosition.y
            if (currentPosition?.y === 'top') {
                positions.originY = 'bottom';
                positions.overlayY = 'top';  // The overlay appears below the origin
            } else if (currentPosition?.y === 'bottom') {
                positions.originY = 'top';
                positions.overlayY = 'bottom';  // The overlay appears above the origin
            } else if (currentPosition?.y === 'center') {
                positions.originY = 'center';
                positions.overlayY = 'center';
            }
        }

        return this._overlay.position()
            .flexibleConnectedTo(this._dialerOrigin.nativeElement)
            .withLockedPosition(true)
            .withPush(true)
            .withPositions([positions]);
    }

    private _handleCallEvent(eventData: any): void {
        if (!eventData.eventType) { return; }
        switch (eventData.eventType) {
            case 'incoming_call':
            case 'dial_number':
                if (eventData.eventType === 'incoming_call') {
                    // Check if call is on another tab first
                    if (this.isUserOnCallOtherTab) {
                        break;
                    }

                    // Extract call details first (before setting onCalling)
                    if (eventData.eventDetails) {
                        this.isCallFromCustomer = eventData.eventDetails.isCallFromCustomer;
                        this.callerName = eventData.eventDetails.fromName;
                        this.callToName = this.callerName || eventData.eventDetails.toName;
                    }

                    // CRITICAL: Show overlay BEFORE setting onCalling flag
                    // Otherwise togglePanel's early return will block display
                    if (!this.toggleOverlay) {
                        this.togglePanel(true);
                    }

                    // Handle hidden document state - set flag to show dialer when user returns
                    if (document.visibilityState === 'hidden') {
                        sessionStorage.setItem('showDialerOnFocus', 'true');
                    }

                    // Maximize if needed (method now has null guard)
                    if (this.isZuperConnectMinimized) {
                        this._maximizeZuperConnectFrame();
                    }

                    // Set incoming call state AFTER overlay is visible
                    this.onCalling = true;

                    // Check if we should show sidekick
                    const shouldShowSidekick = this.callerName && this.isSideKickEnabled &&
                                              document.visibilityState !== 'hidden' &&
                                              !this.isContextualDisplayOpen;

                    // AFTER overlay zoom animation completes (350ms):
                    // 1. Send toggle event to iframe first
                    // 2. Then trigger width animation by setting the flag
                    if (shouldShowSidekick) {
                        setTimeout(() => {
                            // Step 1: Notify iframe first
                            this._sendMessageToZuperConnectFrame({
                                event: 'TOGGLE_CUSTOMER_INFO',
                                data: {}
                            });
                            // Step 2: Then trigger width transformation
                            setTimeout(() => {
                                this.isContextualDisplayOpen = true;
                            }, 50); // Small delay to ensure iframe processes event first
                        }, 350);
                    }
                } else if (eventData.eventType === 'dial_number') {
                    if (!this.toggleOverlay) {
                        this.togglePanel(true);
                    }

                    if (this.integrations?.['zuperconnect']) {
                        this._sendMessageToZuperConnectFrame({
                            event: 'INITIATE_CALL',
                            data: eventData?.eventDetails
                        });
                    }
                }
                break;
            case 'outgoing_call':
                this.onCalling = true;
                /**
                 * Handle outgoing call to customer:
                 * - Check if call has a target number (customer call)
                 * - Verify sidekick is enabled for contextual display
                 * - Send iframe event BEFORE width transformation
                 */
                if (eventData.eventDetails?.toNumber && this.isSideKickEnabled && !this.isContextualDisplayOpen) {
                    this.isCallToCustomer = true;
                    this.callFromName = eventData.eventDetails?.fromName || null;

                    // Step 1: Send toggle event to iframe first
                    this._sendMessageToZuperConnectFrame({
                        event: 'TOGGLE_CUSTOMER_INFO',
                        data: {}
                    });

                    // Step 2: Then trigger width transformation after iframe processes event
                    setTimeout(() => {
                        this.isContextualDisplayOpen = true;
                    }, 50);
                }
                break;
            case 'incoming_call_accepted':
            case 'outgoing_call_accepted':
                this.onCalling = false;
                this.onCall = true;
                break;
            case 'call_end':
            case 'call_rejected':
                this.onCalling = false;
                this.onCall = false;
                if (this.isZuperConnectMinimized) {
                    this.hideOrMinimizeZuperConnectFrame();
                }
                if (this.isSideKickEnabled) {
                    this.closeContextualDisplay();
                }
                this.isCallFromCustomer = null;
                this.isCallToCustomer = null;
                this.callFromName = null;
                this.callToName = null;
                this.callerName = null;
                break;
            case 'minimize_zuper_connect':
                if (!this.isZuperConnectMinimized) {
                    this.minimizeZuperConnectFrame();
                }
                break;
            case 'maximize_zuper_connect':
                if (this.isZuperConnectMinimized) {
                    if ((this.onCall || this.onCalling) && this.isSideKickEnabled) {
                        this.toggleContextualDisplay();
                    }
                    this._maximizeZuperConnectFrame();
                }
                break;
            case 'agent_status':
                this.currentUserStatus = eventData.status;
                this.isUserAvailable = this.currentUserStatus === 'ONLINE';

                // Handle overlay display based on agent status
                if (this._overlayRef) {
                    // Check if dialer should open (not minimized in any tab)
                    const shouldOpenInThisTab = this._dialerService.shouldOpenDialerInCurrentTab();

                    // Case 1: Initial load - show overlay only if:
                    // - Dialer is not minimized
                    // - Not BUSY
                    // - Not already showing
                    if (this.shouldOpenDialer && !this.toggleOverlay && this.currentUserStatus !== 'BUSY' && !this.isZuperConnectMinimized && shouldOpenInThisTab) {
                        this.shouldOpenDialer = false;

                        // Clear minimized state since we're opening it
                        this._dialerService.clearDialerMinimizedState();

                        this._overlayRef.overlayElement.style.display = 'block';
                        setTimeout(() => {
                            this._overlayRef.overlayElement.classList.remove('zoom-out');
                            this._overlayRef.overlayElement.classList.add('zoom-in');
                        }, 500);
                    } else {
                        this.shouldOpenDialer = false;
                    }
                }
                break;
            case 'theme':
                this.isLightThemed = eventData.theme === "light"
                break;
            case 'open_in_zuper':
                this.hideOrMinimizeZuperConnectFrame();
                break;
        }
    }

    private minimizeZuperConnectFrame(): void {
        if (!this._overlayRef) {
            return;
        }

        this.isZuperConnectMinimized = true;
        const minimizedPosition = this._overlay.position()
            .flexibleConnectedTo(this._dialerOrigin.nativeElement)
            .withLockedPosition(false)
            .withPush(true)
            .withPositions([{
                'originX': 'start',
                'originY': 'center',
                'overlayX': 'end',
                'overlayY': 'center',
                'offsetX': 0,
                'offsetY': 2.5,
            }]);
        this._overlayRef.updatePositionStrategy(minimizedPosition);
        this._overlayRef.updatePosition();
        this._dragger.nativeElement.style.transform = 'translate3d(0px, 0px, 0px)';
    }

    private _maximizeZuperConnectFrame(): void {
        if (!this._overlayRef) {
            return;
        }

        this._overlayRef.updatePositionStrategy(this._getMaximizedPosition());
        this._overlayRef.updatePosition();
        this.isZuperConnectMinimized = false;
    }

    private _sendMessageToZuperConnectFrame(messagePayload: any): void {
        const frameWindow = this._zuperConnectIframe.nativeElement.contentWindow;
        const regex = /^(https?:\/\/[^\/]+)/;
        const match = environment.callerUrl.match(regex);
        const origin = match ? match[0] : null;
        // Flag to check whether message from parent window
        // Temp solution to filter messages as webapp and iframe origin will be same when deployed
        messagePayload['is_from_parent'] = true;
        if (origin) {
            frameWindow?.postMessage(
                messagePayload,
                origin
            );
        }
    }

    hideOrMinimizeZuperConnectFrame(): void {
        if (this.isZuperConnectMinimizing) {
            return;
        }
        this.isZuperConnectMinimizing = true;
        if (this.onCall || this.onCalling) {
            // Always hide contextual display if it's open (idempotent operation)
            if (this.isContextualDisplayOpen && this.isSideKickEnabled) {
                this.isContextualDisplayOpen = false;
                this._sendMessageToZuperConnectFrame({
                    event: 'TOGGLE_CUSTOMER_INFO',
                    data: {}
                });
            }
            // Always send the same message to iframe
            this._sendMessageToZuperConnectFrame({
                event: 'MINIMIZED_ON_CALL',
            });
            this.minimizeZuperConnectFrame();
        } else {
            // Set dialer as minimized so other tabs won't open it
            this._dialerService.setDialerMinimized();
            this.togglePanel(false);
            this._dialerService.overlayHiddenSub$.next(true);
        }
        this.isZuperConnectMinimizing = false;
    }

    @HostListener('document:visibilitychange', ['$event'])
    onDocumentVisibilityChange(_: Event): void {
        if (!this._overlayRef) {
            return;
        }
        const minmizedOnBlur = sessionStorage.getItem('minmizedDialerFrameOnBlur');
        if (document.visibilityState === 'hidden') {
            if (this.toggleOverlay && !(this.onCall || this.onCalling)) {
                this.hideOrMinimizeZuperConnectFrame();
                this._dialerService.updateSessionStorageForBrowserVisibility('BLUR');
            }
        } else if (minmizedOnBlur) {
            const shouldShowDialer = sessionStorage.getItem('showDialerOnFocus') || false;

            // Show overlay FIRST before any other operations
            this.togglePanel(true);

            // Then handle maximize and sidekick display
            if (shouldShowDialer || (this.isZuperConnectMinimized && (this.onCall || this.onCalling))) {
                // If there's an incoming call or active call, maximize for visibility
                this._maximizeZuperConnectFrame();

                if (shouldShowDialer) {
                    sessionStorage.removeItem('showDialerOnFocus');

                    // Check if sidekick should be shown (onCalling = incoming call on refocus)
                    const shouldShowSidekick = this.onCalling &&
                                              this.isSideKickEnabled &&
                                              this.callerName &&
                                              !this.isContextualDisplayOpen;

                    // After overlay zoom animation completes (350ms), show sidekick with proper sequencing
                    if (shouldShowSidekick) {
                        setTimeout(() => {
                            // Step 1: Send iframe message first
                            this._sendMessageToZuperConnectFrame({
                                event: 'TOGGLE_CUSTOMER_INFO',
                                data: {}
                            });
                            // Step 2: Then trigger width animation after iframe processes event
                            setTimeout(() => {
                                this.isContextualDisplayOpen = true;
                            }, 50);
                        }, 350);
                    }
                }
            }

            this._dialerService.updateSessionStorageForBrowserVisibility('FOCUS');
            this._dialerService.overlayHiddenSub$.next(false);
        }
    }

    toggleContextualDisplay(): void {
        this.isContextualDisplayOpen = !this.isContextualDisplayOpen;
        this._sendMessageToZuperConnectFrame({
            event: 'TOGGLE_CUSTOMER_INFO',
            data: {}
        });
    }

    closeContextualDisplay(): void {
        this.isContextualDisplayOpen = false;
    }
}
